import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../state/AuthContext";
import { LOCAL_SEED } from "../data/localSeed";
import { SEED_HELP_TICKETS } from "../data/helpdeskSeed";
import { DEMO_EMPLOYEE } from "../lib/roles";
import { PageHeader, RequestRoutingPanel } from "../components/RequestRouting";
import {
  DOMAIN_OWNERS,
  GOVSUITE_SUPPORT,
  HELP_DESK_QUEUES,
  isGovSuiteEmail,
  isProjectFinanceTopic,
  loadHelpDeskTickets,
  rankFaqs,
  resolveAssignee,
  saveHelpDeskTickets,
  suggestHelpDeskQueue,
  ticketRoutingSteps,
  type HelpDeskQueue,
  type HelpDeskTicket,
  type ProjectFinanceRef,
} from "../lib/helpdesk";

type Tab = "new" | "mine" | "assigned" | "tier2";

type SubmitSuccess = {
  id: string;
  subject: string;
  assignee_name: string;
};

function projectsAsFinanceRefs(): ProjectFinanceRef[] {
  return LOCAL_SEED.projects.map((p) => ({
    id: String(p.id),
    code: String(p.code),
    finance_assignee_employee_id: String(p.finance_assignee_employee_id || ""),
    finance_assignee_name: String(p.finance_assignee_name || ""),
  }));
}

export function HelpDeskPage() {
  const { moduleIds, homePath, roleIds, user, effectiveEmail } = useAuth();
  const [tab, setTab] = useState<Tab>("new");
  const [tickets, setTickets] = useState<HelpDeskTicket[]>(() =>
    loadHelpDeskTickets(SEED_HELP_TICKETS),
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState<HelpDeskQueue>("IT");
  const [userOverride, setUserOverride] = useState(false);
  const [projectId, setProjectId] = useState("");
  const [rerouteCat, setRerouteCat] = useState<HelpDeskQueue>("IT");
  const [rerouteReason, setRerouteReason] = useState("");
  const [resolveNote, setResolveNote] = useState("");
  const [escalateReason, setEscalateReason] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState<SubmitSuccess | null>(null);

  const projects = useMemo(() => projectsAsFinanceRefs(), []);
  const suggestText = `${subject} ${body}`;
  const suggested = suggestHelpDeskQueue(suggestText);
  const needsProject = isProjectFinanceTopic(suggestText, category);

  useEffect(() => {
    if (!userOverride) setCategory(suggested);
  }, [suggested, userOverride]);

  useEffect(() => {
    saveHelpDeskTickets(tickets);
  }, [tickets]);

  if (!moduleIds.includes("helpdesk")) return <Navigate to={homePath} replace />;

  const myEmail = (effectiveEmail || user?.email || DEMO_EMPLOYEE.email).toLowerCase();
  const requesterId = DEMO_EMPLOYEE.id;
  /** Demo persona for Tier 1 inbox — roles map to named domain owners. */
  const actingId = (() => {
    if (roleIds.includes("ROLE-GOVSUITE-SUPPORT") && isGovSuiteEmail(effectiveEmail || user?.email)) {
      return GOVSUITE_SUPPORT.employee_id;
    }
    if (
      roleIds.includes("ROLE-PROJECT-MANAGER") ||
      roleIds.includes("ROLE-SUPERVISOR") ||
      roleIds.includes("ROLE-CONTRACTS-SUBCONTRACTS")
    ) {
      return "emp-ortiz";
    }
    if (
      roleIds.includes("ROLE-HR") ||
      roleIds.includes("ROLE-FINANCE-ACCOUNTING") ||
      roleIds.includes("ROLE-PAYROLL") ||
      roleIds.includes("ROLE-HR-PAYROLL")
    ) {
      return "emp-blake";
    }
    if (roleIds.includes("ROLE-AP-PROCUREMENT-INVENTORY")) return "emp-4";
    return DEMO_EMPLOYEE.id;
  })();
  const actingName =
    actingId === "emp-ortiz"
      ? "Jordan Ortiz"
      : actingId === "emp-blake"
        ? "Morgan Blake"
        : actingId === "emp-4"
          ? "Riley Nguyen"
          : actingId === GOVSUITE_SUPPORT.employee_id
            ? GOVSUITE_SUPPORT.name
            : DEMO_EMPLOYEE.name;

  const canTier2 =
    isGovSuiteEmail(effectiveEmail || user?.email) &&
    roleIds.includes("ROLE-GOVSUITE-SUPPORT");
  const canCompanyAdmin = roleIds.includes("ROLE-COMPANY-ADMIN");

  const mine = tickets
    .filter(
      (t) => t.requester_id === requesterId || t.requester_email.toLowerCase() === myEmail,
    )
    .sort((a, b) => b.opened_at.localeCompare(a.opened_at));

  const assigned = tickets
    .filter(
      (t) =>
        t.tier === 1 &&
        t.status !== "Resolved" &&
        t.status !== "Closed" &&
        (t.assignee_id === actingId || canCompanyAdmin),
    )
    .sort((a, b) => b.opened_at.localeCompare(a.opened_at));

  const tier2 = tickets
    .filter((t) => t.tier === 2 && t.status !== "Resolved" && t.status !== "Closed")
    .sort((a, b) => b.opened_at.localeCompare(a.opened_at));

  const list =
    tab === "mine" ? mine : tab === "assigned" ? assigned : tab === "tier2" ? tier2 : [];
  const selected =
    tab === "new" ? null : tickets.find((t) => t.id === selectedId) ?? list[0] ?? null;
  const faqs = rankFaqs(suggestText);
  const previewOwner = resolveAssignee(
    category,
    suggestText,
    projects.find((p) => p.id === projectId) ?? null,
  );

  function persist(next: HelpDeskTicket[]) {
    setTickets(next);
    saveHelpDeskTickets(next);
  }

  function patchTicket(id: string, fn: (t: HelpDeskTicket) => HelpDeskTicket) {
    persist(tickets.map((t) => (t.id === id ? fn(t) : t)));
  }

  function resetNewRequestForm() {
    setSubject("");
    setBody("");
    setProjectId("");
    setUserOverride(false);
    setCategory("IT");
    setSubmitSuccess(null);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return;
    if (needsProject && !projectId) {
      window.alert("Select the project this finance question is about.");
      return;
    }
    const project = projects.find((p) => p.id === projectId) ?? null;
    const assignee = resolveAssignee(category, suggestText, project);
    const now = new Date().toISOString();
    const id = `hd-${Date.now()}`;
    const subjectText = subject.trim();
    const row: HelpDeskTicket = {
      id,
      subject: subjectText,
      body: body.trim(),
      category,
      status: "Submitted",
      tier: 1,
      requester_id: requesterId,
      requester_email: myEmail,
      requester_name: DEMO_EMPLOYEE.name,
      assignee_id: assignee.employee_id,
      assignee_name: assignee.name,
      project_id: assignee.project_id,
      project_code: assignee.project_code,
      opened_at: now,
      route_history: [
        {
          at: now,
          kind: "assigned",
          from_name: DEMO_EMPLOYEE.name,
          to_name: assignee.name,
          reason: assignee.project_code
            ? `Project finance assignee for ${assignee.project_code}`
            : `${category} domain owner`,
        },
      ],
    };
    persist([row, ...tickets]);
    setSubmitSuccess({
      id,
      subject: subjectText,
      assignee_name: assignee.name,
    });
  }

  function goToSubmittedRequest(id: string) {
    setSelectedId(id);
    setTab("mine");
    setSubmitSuccess(null);
    setSubject("");
    setBody("");
    setProjectId("");
    setUserOverride(false);
  }

  function acceptTicket(t: HelpDeskTicket) {
    const now = new Date().toISOString();
    patchTicket(t.id, (x) => ({
      ...x,
      status: "In progress",
      accepted_at: now,
      route_history: [
        ...x.route_history,
        {
          at: now,
          kind: "accept",
          from_name: actingName,
          to_name: x.assignee_name,
          reason: "Accepted",
        },
      ],
    }));
  }

  function rerouteTicket(t: HelpDeskTicket) {
    if (!rerouteReason.trim()) {
      window.alert("Add a reason for the re-route (visible to the requester).");
      return;
    }
    const owner = DOMAIN_OWNERS[rerouteCat];
    const now = new Date().toISOString();
    patchTicket(t.id, (x) => ({
      ...x,
      category: rerouteCat,
      tier: 1,
      status: "Submitted",
      assignee_id: owner.employee_id,
      assignee_name: owner.name,
      route_history: [
        ...x.route_history,
        {
          at: now,
          kind: "reroute",
          from_name: actingName,
          to_name: owner.name,
          reason: rerouteReason.trim(),
        },
      ],
    }));
    setRerouteReason("");
  }

  function escalateTicket(t: HelpDeskTicket) {
    if (!escalateReason.trim()) {
      window.alert("Add an escalation reason (visible on the routing path).");
      return;
    }
    const now = new Date().toISOString();
    patchTicket(t.id, (x) => ({
      ...x,
      tier: 2,
      status: "Submitted",
      assignee_id: GOVSUITE_SUPPORT.employee_id,
      assignee_name: GOVSUITE_SUPPORT.name,
      route_history: [
        ...x.route_history,
        {
          at: now,
          kind: "escalate",
          from_name: actingName,
          to_name: GOVSUITE_SUPPORT.name,
          reason: escalateReason.trim(),
        },
      ],
    }));
    setEscalateReason("");
  }

  function resolveTicket(t: HelpDeskTicket) {
    if (!resolveNote.trim()) {
      window.alert("Add a resolution note.");
      return;
    }
    const now = new Date().toISOString();
    patchTicket(t.id, (x) => ({
      ...x,
      status: "Resolved",
      resolved_at: now,
      resolve_note: resolveNote.trim(),
      route_history: [
        ...x.route_history,
        {
          at: now,
          kind: "resolve",
          from_name: actingName,
          to_name: x.requester_name,
          reason: resolveNote.trim(),
        },
      ],
    }));
    setResolveNote("");
  }

  const canActOnSelected =
    !!selected &&
    selected.status !== "Resolved" &&
    selected.status !== "Closed" &&
    (tab === "assigned" || tab === "tier2") &&
    (canCompanyAdmin ||
      selected.assignee_id === actingId ||
      (selected.tier === 2 && canTier2));

  return (
    <div className="space-y-5">
      <PageHeader
        title="Help desk"
        subtitle="Tickets go to the right named person — domain owners or project finance assignees. Product issues escalate to GovSuite (Tier 2)."
      />

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => {
            setTab("new");
            setSubmitSuccess(null);
          }}
          className={`px-3 py-1.5 text-sm ${
            tab === "new" ? "bg-navy text-white" : "bg-white border border-black/10 text-navy"
          }`}
        >
          New request
        </button>
        <button
          type="button"
          onClick={() => setTab("mine")}
          className={`px-3 py-1.5 text-sm ${
            tab === "mine" ? "bg-navy text-white" : "bg-white border border-black/10 text-navy"
          }`}
        >
          My requests ({mine.length})
        </button>
        <button
          type="button"
          onClick={() => setTab("assigned")}
          className={`px-3 py-1.5 text-sm ${
            tab === "assigned" ? "bg-navy text-white" : "bg-white border border-black/10 text-navy"
          }`}
        >
          Assignments as {actingName} ({assigned.length})
        </button>
        {canTier2 && (
          <button
            type="button"
            onClick={() => setTab("tier2")}
            className={`px-3 py-1.5 text-sm ${
              tab === "tier2" ? "bg-navy text-white" : "bg-white border border-black/10 text-navy"
            }`}
          >
            GovSuite Support Tier 2 ({tier2.length})
          </button>
        )}
      </div>

      {tab === "new" && (
        <div className="max-w-xl space-y-4">
          {submitSuccess ? (
            <div className="bg-white border border-black/5 p-5 space-y-4">
              <div className="space-y-1">
                <p className="text-sm font-medium text-teal">Request submitted</p>
                <h2 className="font-display text-xl text-navy">{submitSuccess.subject}</h2>
                <p className="text-sm text-slate">
                  Routed to <strong className="text-navy">{submitSuccess.assignee_name}</strong>.
                  You can open it under My requests or submit another.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="px-4 py-2 text-sm bg-navy text-white"
                  onClick={() => goToSubmittedRequest(submitSuccess.id)}
                >
                  View my request
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm border border-black/10 text-navy"
                  onClick={resetNewRequestForm}
                >
                  Submit another request
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="bg-white border border-black/5 p-4 space-y-3">
              <h2 className="font-semibold text-navy">New request</h2>
              <label className="block text-sm font-medium text-navy">
                Subject
                <input
                  className="mt-1 w-full border border-black/10 px-3 py-2"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                />
              </label>
              <label className="block text-sm font-medium text-navy">
                Details
                <textarea
                  className="mt-1 w-full border border-black/10 px-3 py-2 min-h-[100px]"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  required
                />
              </label>
              <label className="block text-sm font-medium text-navy">
                Category
                <select
                  className="mt-1 w-full border border-black/10 px-3 py-2"
                  value={category}
                  onChange={(e) => {
                    setUserOverride(true);
                    setCategory(e.target.value as HelpDeskQueue);
                  }}
                >
                  {HELP_DESK_QUEUES.map((q) => (
                    <option key={q} value={q}>
                      {q} — {DOMAIN_OWNERS[q].name}
                    </option>
                  ))}
                </select>
              </label>
              <p className="text-xs text-slate">
                Suggested: <strong className="text-navy">{suggested}</strong>
                {!userOverride ? " (auto)" : " (overridden)"}. Routes to{" "}
                <strong className="text-navy">{previewOwner.name}</strong>
                {previewOwner.project_code ? ` (${previewOwner.project_code} finance)` : ""}.
              </p>
              {needsProject && (
                <label className="block text-sm font-medium text-navy">
                  Project
                  <select
                    className="mt-1 w-full border border-black/10 px-3 py-2"
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    required
                  >
                    <option value="">Select project…</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.code} — finance: {p.finance_assignee_name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <button type="submit" className="px-4 py-2 bg-navy text-white text-sm">
                Submit ticket
              </button>
              {faqs.length > 0 && (
                <div className="border-t border-black/5 pt-3 space-y-2">
                  <div className="text-xs uppercase tracking-wider text-slate">Related FAQ</div>
                  {faqs.slice(0, 3).map((f) => (
                    <div key={f.id} className="text-sm">
                      <div className="font-medium text-navy">{f.title}</div>
                      <p className="text-slate text-xs mt-0.5">{f.body}</p>
                    </div>
                  ))}
                </div>
              )}
            </form>
          )}
        </div>
      )}

      {(tab === "mine" || tab === "assigned" || tab === "tier2") && (
        <div className="grid gap-4 lg:grid-cols-[minmax(260px,340px)_minmax(0,1fr)] lg:items-start">
          <TicketList
            tickets={list}
            selectedId={selected?.id ?? null}
            onSelect={setSelectedId}
            empty={
              tab === "mine"
                ? "You have not submitted any tickets yet."
                : tab === "tier2"
                  ? "No open Tier 2 escalations."
                  : "No tickets assigned to you right now."
            }
          />

          {selected ? (
            <div className="bg-white border border-black/5 p-4 space-y-4 min-w-0">
              <div className="flex flex-wrap justify-between gap-2">
                <div>
                  <h2 className="font-semibold text-navy">{selected.subject}</h2>
                  <p className="text-sm text-slate mt-1">
                    Tier {selected.tier} · {selected.category}
                    {selected.project_code ? ` · ${selected.project_code}` : ""} · Assignee{" "}
                    <strong className="text-navy">{selected.assignee_name}</strong> ·{" "}
                    {selected.status}
                  </p>
                  <p className="text-sm text-ink/90 mt-2 whitespace-pre-wrap">{selected.body}</p>
                  {selected.resolve_note && (
                    <p className="text-sm text-teal mt-2">Resolution: {selected.resolve_note}</p>
                  )}
                </div>
              </div>

              <RequestRoutingPanel title="Routing" steps={ticketRoutingSteps(selected)} />

              {canActOnSelected &&
                selected.status !== "Resolved" &&
                selected.status !== "Closed" &&
                (tab === "assigned" || tab === "tier2") && (
                  <div className="grid md:grid-cols-2 gap-4 border-t border-black/5 pt-4">
                    {selected.status === "Submitted" && (
                      <button
                        type="button"
                        className="px-3 py-2 text-sm bg-navy text-white"
                        onClick={() => acceptTicket(selected)}
                      >
                        Accept
                      </button>
                    )}

                    {selected.tier === 1 && (
                      <>
                        <div className="space-y-2 md:col-span-2">
                          <div className="text-sm font-medium text-navy">Re-route (transparent)</div>
                          <div className="flex flex-wrap gap-2">
                            <select
                              className="border border-black/10 px-2 py-1.5 text-sm"
                              value={rerouteCat}
                              onChange={(e) => setRerouteCat(e.target.value as HelpDeskQueue)}
                            >
                              {HELP_DESK_QUEUES.map((q) => (
                                <option key={q} value={q}>
                                  {q} — {DOMAIN_OWNERS[q].name}
                                </option>
                              ))}
                            </select>
                            <input
                              className="flex-1 min-w-[180px] border border-black/10 px-2 py-1.5 text-sm"
                              placeholder="Reason (shown to requester)"
                              value={rerouteReason}
                              onChange={(e) => setRerouteReason(e.target.value)}
                            />
                            <button
                              type="button"
                              className="px-3 py-1.5 text-sm border border-black/10"
                              onClick={() => rerouteTicket(selected)}
                            >
                              Re-route
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <div className="text-sm font-medium text-navy">
                            Escalate to GovSuite (Tier 2)
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <input
                              className="flex-1 min-w-[180px] border border-black/10 px-2 py-1.5 text-sm"
                              placeholder="Why this is a product / technical issue"
                              value={escalateReason}
                              onChange={(e) => setEscalateReason(e.target.value)}
                            />
                            <button
                              type="button"
                              className="px-3 py-1.5 text-sm bg-gold/30 text-navy"
                              onClick={() => escalateTicket(selected)}
                            >
                              Escalate
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    <div className="space-y-2 md:col-span-2">
                      <div className="text-sm font-medium text-navy">Resolve</div>
                      <div className="flex flex-wrap gap-2">
                        <input
                          className="flex-1 min-w-[180px] border border-black/10 px-2 py-1.5 text-sm"
                          placeholder="Resolution note"
                          value={resolveNote}
                          onChange={(e) => setResolveNote(e.target.value)}
                        />
                        <button
                          type="button"
                          className="px-3 py-1.5 text-sm bg-teal text-white"
                          onClick={() => resolveTicket(selected)}
                        >
                          Resolve
                        </button>
                      </div>
                    </div>
                  </div>
                )}
            </div>
          ) : (
            <div className="bg-white border border-black/5 p-4 text-sm text-slate hidden lg:block">
              Select a ticket to see details and routing.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TicketList({
  tickets,
  selectedId,
  onSelect,
  empty,
}: {
  tickets: HelpDeskTicket[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  empty: string;
}) {
  if (!tickets.length) {
    return (
      <div className="bg-white border border-black/5 p-4 text-sm text-slate">{empty}</div>
    );
  }
  return (
    <ul className="bg-white border border-black/5 divide-y divide-black/5 max-h-80 lg:max-h-[min(70vh,36rem)] overflow-y-auto">
      {tickets.map((t) => (
        <li key={t.id}>
          <button
            type="button"
            onClick={() => onSelect(t.id)}
            className={`w-full text-left px-3 py-2.5 text-sm hover:bg-teal-soft/40 ${
              selectedId === t.id ? "bg-teal-soft border-l-4 border-l-teal" : "border-l-4 border-l-transparent"
            }`}
          >
            <div className="font-medium text-navy">{t.subject}</div>
            <div className="text-xs text-slate mt-0.5">
              Tier {t.tier} · {t.assignee_name} · {t.status}
              {t.project_code ? ` · ${t.project_code}` : ""}
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}
